import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  Paper,
  Typography,
} from "@mui/material";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { darken } from "@mui/material/styles";
import { differenceInMinutes } from "date-fns";
import QuickActionDial from "./QuickActionDial";
import useQuickActionPrefs from "../../hooks/useQuickActionPrefs";
import {
  formatTimeNoSeconds,
  formatDay,
  formatDurationNoSeconds,
  formatDurationWithSeconds,
  getFeedingDurationLabel,
  groupLogByDay,
  CONSISTENCY_OPTIONS,
  getMedicineChipSx,
} from "./trackerHelpers";

const LogTab = ({
  log,
  activeFeeding,
  activeSleep,
  activeTummyTime,
  onStopFeeding,
  onStopSleep,
  onStopTummyTime,
  onStartFeeding,
  onStartSleep,
  onStartTummyTime,
  onAddDiaper,
  onStartExtractedMilk,
  onAddMedicine,
  onEditEntry,
}) => {
  const { t } = useTranslation();
  const [filterTypes, setFilterTypes] = useState([]);
  const [imageDialogUrl, setImageDialogUrl] = useState(null);
  const { visibleActions } = useQuickActionPrefs();

  const toggleFilterType = (type) => {
    setFilterTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  return (
    <Box
      sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2, pb: 10 }}
    >
      {Object.keys(groupLogByDay(log))
        .sort((a, b) => new Date(b) - new Date(a))
        .map((day) => {
          const dayEntries = groupLogByDay(log)[day];

          const visibleEntries =
            filterTypes.length === 0
              ? dayEntries
              : dayEntries.filter((e) => filterTypes.includes(e.type));

          const sleepEntries = dayEntries.filter(
            (entry) => entry.type === "Sleep",
          );
          const totalMinutes = sleepEntries.reduce((sum, entry) => {
            if (!entry.start || !entry.end) return sum;
            return (
              sum +
              differenceInMinutes(new Date(entry.end), new Date(entry.start))
            );
          }, 0);
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          const formattedDuration =
            totalMinutes > 0
              ? hours > 0
                ? ` (${hours}h ${minutes}m)`
                : ` (${minutes}m)`
              : "";

          const sortedEntries = [...visibleEntries].sort(
            (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime(),
          );

          return (
            <Paper
              key={day}
              elevation={10}
              sx={{ borderRadius: 2, overflow: "hidden", bgcolor: "background.card" }}
            >
              {/* Day header */}
              <Box
                sx={{
                  backgroundColor: darken("#8fb9a8", 0.2),
                  color: "white",
                  textAlign: "center",
                  py: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="bold"
                >
                  {formatDay(visibleEntries[0]?.start)}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mt: 0.5,
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {visibleActions.includes("sleep") && (
                    <Chip
                      label={`😴 ${dayEntries.filter((e) => e.type === "Sleep").length}${formattedDuration}`}
                      size="small"
                      clickable
                      onClick={() => toggleFilterType("Sleep")}
                      variant={
                        filterTypes.includes("Sleep") ? "filled" : "outlined"
                      }
                      sx={{
                        backgroundColor: filterTypes.includes("Sleep")
                          ? "#4c706d"
                          : undefined,
                        color: filterTypes.includes("Sleep")
                          ? "white"
                          : undefined,
                      }}
                    />
                  )}
                  {visibleActions.includes("diaper") && (
                    <Chip
                      label={`💩 ${dayEntries.filter((e) => e.type === "Diaper").length}`}
                      size="small"
                      clickable
                      onClick={() => toggleFilterType("Diaper")}
                      variant={
                        filterTypes.includes("Diaper") ? "filled" : "outlined"
                      }
                      sx={{
                        backgroundColor: filterTypes.includes("Diaper")
                          ? "#4c706d"
                          : undefined,
                        color: filterTypes.includes("Diaper")
                          ? "white"
                          : undefined,
                      }}
                    />
                  )}
                  {visibleActions.includes("tummyTime") && (
                    <Chip
                      label={`🧸 ${dayEntries.filter((e) => e.type === "Tummy Time").length}`}
                      size="small"
                      clickable
                      onClick={() => toggleFilterType("Tummy Time")}
                      variant={
                        filterTypes.includes("Tummy Time")
                          ? "filled"
                          : "outlined"
                      }
                      sx={{
                        backgroundColor: filterTypes.includes("Tummy Time")
                          ? "#4c706d"
                          : undefined,
                        color: filterTypes.includes("Tummy Time")
                          ? "white"
                          : undefined,
                      }}
                    />
                  )}
                  {visibleActions.includes("medicine") && (() => {
                    const medicineCount = dayEntries.filter((e) => e.type === "Medicine").length;
                    const hasDDrops = dayEntries.some(
                      (e) =>
                        (e.type === "Feeding" && e.dVitamin) ||
                        (e.type === "Medicine" &&
                          Array.isArray(e.medicines) &&
                          e.medicines.includes("D-dråber")),
                    );
                    const isFiltered = filterTypes.includes("Medicine");
                    return (
                      <Chip
                        label={`💊 ${medicineCount}`}
                        size="small"
                        clickable
                        onClick={() => toggleFilterType("Medicine")}
                        variant={isFiltered ? "filled" : "outlined"}
                        sx={{
                          backgroundColor: isFiltered
                            ? "#4c706d"
                            : !hasDDrops
                            ? "var(--error-color)"
                            : undefined,
                          color: isFiltered || !hasDDrops ? "white" : undefined,
                        }}
                      />
                    );
                  })()}
                  {!visibleActions.includes("medicine") && (visibleActions.includes("feeding")) && (
                    <Chip
                      label="💊"
                      size="small"
                      sx={
                        !dayEntries.some((e) => e.type === "Feeding" && e.dVitamin)
                          ? { backgroundColor: "var(--error-color)", color: "white" }
                          : {}
                      }
                    />
                  )}
                  {visibleActions.includes("feeding") && (
                    <Chip
                      label={`🍼 ${dayEntries.filter((e) => e.type === "Feeding").length} (${dayEntries
                        .filter((e) => e.type === "Feeding")
                        .reduce(
                          (sum, e) =>
                            sum + (e.extraMilkMM || 0) + (e.extraMilkMME || 0),
                          0,
                        )}ml)`}
                      size="small"
                      clickable
                      onClick={() => toggleFilterType("Feeding")}
                      variant={
                        filterTypes.includes("Feeding") ? "filled" : "outlined"
                      }
                      sx={{
                        backgroundColor: filterTypes.includes("Feeding")
                          ? "#4c706d"
                          : undefined,
                        color: filterTypes.includes("Feeding")
                          ? "white"
                          : undefined,
                      }}
                    />
                  )}
                  {visibleActions.includes("extractedMilk") && (
                    <Chip
                      label={`🥛 ${dayEntries.filter((e) => e.type === "Extracted Milk").length} (${dayEntries
                        .filter((e) => e.type === "Extracted Milk")
                        .reduce((sum, e) => sum + (e.amount || 0), 0)}ml)`}
                      size="small"
                      clickable
                      onClick={() => toggleFilterType("Extracted Milk")}
                      variant={
                        filterTypes.includes("Extracted Milk")
                          ? "filled"
                          : "outlined"
                      }
                      sx={{
                        backgroundColor: filterTypes.includes("Extracted Milk")
                          ? "#4c706d"
                          : undefined,
                        color: filterTypes.includes("Extracted Milk")
                          ? "white"
                          : undefined,
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Entries */}
              <List disablePadding>
                {sortedEntries.map((entry, index) => {
                  let entryContent;
                  let gapText = "";

                  if (entry.type === "Feeding") {
                    entryContent = (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: entry.background,
                            borderRadius: "50%",
                          }}
                        >
                          {entry.icon}
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {entry.side !== "none" && (
                            <Chip
                              label={
                                entry.side
                                  ? entry.side.charAt(0).toUpperCase() +
                                    entry.side.slice(1)
                                  : ""
                              }
                              size="small"
                            />
                          )}
                          {entry.extraMilkMM > 0 && (
                            <Chip
                              label={`${entry.extraMilkMM} ml`}
                              size="small"
                              sx={{ opacity: entry.done ? 0.4 : 1 }}
                            />
                          )}
                          {entry.extraMilkMME > 0 && (
                            <Chip
                              label={`${entry.extraMilkMME} ml`}
                              size="small"
                              sx={{
                                backgroundColor: "#FCD0BA",
                                color: "black",
                                opacity: entry.done ? 0.4 : 1,
                              }}
                            />
                          )}
                          {entry.solidFoodNote?.length > 0 && (
                            <Chip
                              label={entry.solidFoodNote}
                              size="small"
                              sx={{
                                backgroundColor: "#FFF59D",
                                opacity: entry.done ? 0.4 : 1,
                              }}
                            />
                          )}
                          {entry.dVitamin && (
                            <Chip
                              label="💊"
                              size="small"
                              sx={{ backgroundColor: "#FCD0BA" }}
                            />
                          )}
                          {entry.imageUrl && (
                            <Chip
                              label="📷"
                              size="small"
                              sx={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setImageDialogUrl(entry.imageUrl);
                              }}
                            />
                          )}
                        </Box>
                        <Chip
                          label={getFeedingDurationLabel(entry)}
                          size="small"
                          sx={
                            entry.active
                              ? {
                                  backgroundColor: "var(--error-color)",
                                  color: "white",
                                }
                              : {}
                          }
                        />
                      </Box>
                    );
                  } else if (entry.type === "Sleep") {
                    const effectiveEnd = entry.end
                      ? entry.end
                      : new Date().toISOString();
                    const previousSleep = visibleEntries
                      .filter(
                        (e) =>
                          e.type === "Sleep" &&
                          new Date(e.start) < new Date(entry.start),
                      )
                      .sort(
                        (a, b) =>
                          new Date(b.start).getTime() -
                          new Date(a.start).getTime(),
                      )[0];
                    if (previousSleep?.end) {
                      const diffMs =
                        new Date(entry.start).getTime() -
                        new Date(previousSleep.end).getTime();
                      if (diffMs > 0) {
                        const mins = Math.round(diffMs / 60000);
                        const h = Math.floor(mins / 60);
                        const m = mins % 60;
                        gapText = t("log.awake_gap", { h, m });
                      }
                    }
                    entryContent = (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: darken(entry.background, 0.1),
                            borderRadius: "50%",
                          }}
                        >
                          {entry.icon}
                        </Box>
                        <Chip
                          label={
                            entry.active
                              ? formatDurationWithSeconds(
                                  entry.start,
                                  effectiveEnd,
                                )
                              : formatDurationNoSeconds(
                                  entry.start,
                                  effectiveEnd,
                                )
                          }
                          size="small"
                          sx={
                            entry.active
                              ? {
                                  backgroundColor: "var(--error-color)",
                                  color: "white",
                                }
                              : {}
                          }
                        />
                        {(entry.headPosition === "left" ||
                          entry.headPosition === "right") && (
                          <Chip
                            label={
                              entry.headPosition === "left"
                                ? t("log.head_left")
                                : t("log.head_right")
                            }
                            size="small"
                          />
                        )}
                      </Box>
                    );
                  } else if (entry.type === "Diaper") {
                    entryContent = (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: entry.background,
                            borderRadius: "50%",
                          }}
                        >
                          {entry.icon}
                        </Box>
                        {entry.poop === "poop" && (
                          <Chip
                            label={t("log.poop")}
                            size="small"
                            sx={{ backgroundColor: entry.color }}
                          />
                        )}
                        {entry.poop === "poop" &&
                          entry.konsistens &&
                          entry.konsistens !== "normal" &&
                          (() => {
                            const opt = CONSISTENCY_OPTIONS.find(
                              (o) => o.value === entry.konsistens,
                            );
                            return opt ? (
                              <Chip
                                label={opt.label}
                                size="small"
                                sx={{
                                  backgroundColor: opt.color,
                                  color: "#fff",
                                }}
                              />
                            ) : null;
                          })()}
                        {entry.pee === "pee" && (
                          <Chip
                            label={t("log.pee")}
                            size="small"
                          />
                        )}
                        {entry.imageUrl && (
                          <Chip
                            label="📷"
                            size="small"
                            sx={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setImageDialogUrl(entry.imageUrl);
                            }}
                          />
                        )}
                      </Box>
                    );
                  } else if (entry.type === "Medicine") {
                    entryContent = (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: entry.background,
                            borderRadius: "50%",
                          }}
                        >
                          {entry.icon}
                        </Box>
                        {(entry.medicines || []).map((med) => (
                          <Chip
                            key={med}
                            label={med}
                            size="small"
                            sx={getMedicineChipSx(med)}
                          />
                        ))}
                        {entry.fever != null && (() => {
                          const t = entry.fever;
                          const label = t >= 39.0 ? `🌡️ Høj feber ${t}°` : t >= 38.0 ? `🌡️ Feber ${t}°` : t >= 37.6 ? `🌡️ Forhøjet ${t}°` : `🌡️ Normal ${t}°`;
                          const bg = t >= 39.0 ? "#B71C1C" : t >= 38.0 ? "#FF7043" : t >= 37.6 ? "#FF9800" : "#4CAF50";
                          return <Chip label={label} size="small" sx={{ backgroundColor: bg, color: "#fff" }} />;
                        })()}
                      </Box>
                    );
                  } else if (entry.type === "Tummy Time") {
                    const effectiveEnd = entry.end || new Date().toISOString();
                    entryContent = (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: entry.background,
                            borderRadius: "50%",
                          }}
                        >
                          {entry.icon}
                        </Box>
                        <Chip
                          label={
                            entry.active
                              ? formatDurationWithSeconds(
                                  entry.start,
                                  effectiveEnd,
                                )
                              : formatDurationNoSeconds(
                                  entry.start,
                                  effectiveEnd,
                                )
                          }
                          size="small"
                          sx={
                            entry.active
                              ? {
                                  backgroundColor: "var(--error-color)",
                                  color: "white",
                                }
                              : {}
                          }
                        />
                      </Box>
                    );
                  } else if (entry.type === "Extracted Milk") {
                    entryContent = (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: entry.background,
                            borderRadius: "50%",
                          }}
                        >
                          {entry.icon}
                        </Box>
                        <Chip
                          label={`${entry.amount} ml`}
                          size="small"
                        />
                      </Box>
                    );
                  } else if (entry.type === "Weight") {
                    entryContent = (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: entry.background,
                            borderRadius: "50%",
                          }}
                        >
                          {entry.icon}
                        </Box>
                        <Chip
                          label={`${entry.weight} g`}
                          size="small"
                        />
                      </Box>
                    );
                  }

                  const gapChip =
                    entry.type === "Sleep" && gapText ? (
                      <Chip
                        label={gapText}
                        size="small"
                        variant="outlined"
                        sx={{ fontStyle: "italic", bgcolor: "background.card" }}
                      />
                    ) : null;

                  const stopHandler =
                    entry.type === "Feeding"
                      ? onStopFeeding
                      : entry.type === "Sleep"
                        ? onStopSleep
                        : entry.type === "Tummy Time"
                          ? onStopTummyTime
                          : null;

                  return (
                    <Box key={entry.id}>
                      <ListItem
                        divider={!gapChip && index < sortedEntries.length - 1}
                        onClick={() => onEditEntry(entry)}
                        sx={{ cursor: "pointer", alignItems: "center", py: 1 }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {entry.type === "Feeding" ||
                            entry.type === "Sleep" ||
                            entry.type === "Tummy Time"
                              ? `${formatTimeNoSeconds(entry.start)} – ${formatTimeNoSeconds(entry.end || new Date().toISOString())}`
                              : formatTimeNoSeconds(entry.start)}
                          </Typography>
                          {entryContent}
                        </Box>
                        {entry.active && stopHandler && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              stopHandler(e);
                            }}
                            sx={{
                              flexShrink: 0,
                              color: "#F57C00",
                              animation: "pulse 2s ease-in-out infinite",
                              "@keyframes pulse": {
                                "0%, 100%": {
                                  transform: "scale(1)",
                                  boxShadow: "0 0 0 0 rgba(245, 124, 0, 0.7)",
                                },
                                "50%": {
                                  transform: "scale(1.2)",
                                  boxShadow: "0 0 0 6px rgba(245, 124, 0, 0)",
                                },
                              },
                            }}
                          >
                            <StopCircleIcon />
                          </IconButton>
                        )}
                      </ListItem>
                      {gapChip && index < sortedEntries.length - 1 && (
                        <Box
                          sx={{
                            position: "relative",
                            borderBottom: 1,
                            borderColor: "divider",
                            height: 0,
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                            }}
                          >
                            {gapChip}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </List>
            </Paper>
          );
        })}
      <Dialog
        open={!!imageDialogUrl}
        onClose={() => setImageDialogUrl(null)}
        maxWidth="md"
      >
        <DialogContent sx={{ p: 0 }}>
          <img
            src={imageDialogUrl}
            alt="Feeding"
            style={{ display: "block", maxWidth: "100%", maxHeight: "90vh" }}
          />
        </DialogContent>
      </Dialog>
      <QuickActionDial
        onStartFeeding={onStartFeeding}
        onStopFeeding={onStopFeeding}
        activeFeeding={activeFeeding}
        onStartSleep={onStartSleep}
        onStopSleep={onStopSleep}
        activeSleep={activeSleep}
        onStartTummyTime={onStartTummyTime}
        onStopTummyTime={onStopTummyTime}
        activeTummyTime={activeTummyTime}
        onStartDiaper={onAddDiaper}
        onStartExtractedMilk={onStartExtractedMilk}
        onAddMedicine={onAddMedicine}
        visibleActions={visibleActions}
      />
    </Box>
  );
};

export default LogTab;
